/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable consistent-return */
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';

import {
  selectInterventionsChecklistValue,
  calculatorActions,
  selectServiceDataForm,
  selectInterventionsValue,
  selectReportName,
  selectState,
  selectTouched,
} from '../../../../redux/calculatorSlice';

import {
  InterventionsChecklistForm,
  IAsthma,
  InterventionsCommonForm,
  AsthmaForm,
} from '../../../../interfaces/interventions.interface';

import { CalculatorEnum } from '../../../../interfaces/calculator.interface';
import {
  InterventionHeader,
  InterventionBody,
  InterventionFooter,
  InterventionContainer,
  InterventionFieldNoteDialog,
} from '../Intervention';

import { FormValidations, StringFormatter } from '../../../../utils';
import useDebounceEffect from '../../../../hooks/useDebounceEffect';
import { Asthma as AsthmaClass } from '../../../../calculations';
import { paths } from '../../..';
import { Constants } from '../../../../utils';
import { useLocation } from 'react-router';

const { DIALOG_MODES } = Constants;

const Asthma: FunctionComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();

  const switchValue = useSelector(
    selectInterventionsChecklistValue(InterventionsChecklistForm.Asthma)
  );

  const asthmaData = useSelector(
    selectInterventionsValue(CalculatorEnum.Asthma)
  );

  const serviceData = useSelector(selectServiceDataForm);
  const selectedState = useSelector(selectState);
  const reportName = useSelector(selectReportName);
  const touched = useSelector(selectTouched);

  const [reinitialize, setReinitialize] = useState(true);
  const [noteName, setNoteName] = useState<string>('');
  const [noteAction, setNoteAction] = useState<'add' | 'edit' | 'delete'>(
    DIALOG_MODES.ADD
  );
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);

  const fields = [
    {
      text: `Number of Tier 2 and 3 HS Medicaid/CHIP-enrolled children ages 0-3 who have asthma or are wheezing and at risk for asthma
      `,
      id: AsthmaForm.NumberHaveAsthma,
      showError: true,
    },
  ];

  const onSubmit = (values: IAsthma) => {
    if (touched) {
      dispatch(
        calculatorActions.saveReport({
          reportName,
          update: true,
          navigate:navigate,
          pathname:location.pathname
        })
      );
    }

    navigate(
      StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsEmergencyDepartmentUse
      )
    );
  };

  useEffect(() => {
    if (switchValue) {
      const timeout = setTimeout(() => {
        setReinitialize(false);
      }, 250);
      return () => clearTimeout(timeout);
    }
  }, [switchValue]);

  const form = useFormik({
    initialValues: asthmaData,
    onSubmit,
    validationSchema:
      switchValue &&
      FormValidations.intervention({
        [AsthmaForm.NumberHaveAsthma]: FormValidations.interventionNumberOfChilds(
          AsthmaForm.NumberHaveAsthma,
          serviceData.csInput3
        ),
      }),
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: reinitialize || !switchValue,
  });

  useEffect(() => {
    if (!switchValue) {
      form.resetForm();
      dispatch(
        calculatorActions.clearInterventionData(CalculatorEnum.Asthma)
      );
    }
  }, [switchValue]);

  useDebounceEffect(() => {
    const hasBeenEdited = Object.keys(form.values).some(
      (key) => !!form.values[key]
    );

    if (!form.dirty && !hasBeenEdited) return;

    const asthma = new AsthmaClass({
      state: selectedState,
      serviceData,
      lowEstimatedSlider: +form.values[InterventionsCommonForm.LowEstimate],
      highEstimatedSlider: +form.values[InterventionsCommonForm.HighEstimate],
      asthma_numberHaveAsthma: +form.values[AsthmaForm.NumberHaveAsthma],
    });

    const savingsLow = asthma.asthmaEstimatedSavingsLow();
    const savingsHigh = asthma.asthmaEstimatedSavingsHigh();

    if (
      +form.values[InterventionsCommonForm.SavingsLow] !== savingsLow ||
      +form.values[InterventionsCommonForm.SavingsHigh] !== savingsHigh
    ) {
      form.setValues({
        ...form.values,
        [InterventionsCommonForm.SavingsLow]: savingsLow.toString(),
        [InterventionsCommonForm.SavingsHigh]: savingsHigh.toString(),
      });
    }

    form.validateForm().then((errors) => {
      const isValid = Object.keys(errors).length === 0;

      if (form.dirty) {
        dispatch(
          calculatorActions.setInterventionData({
            intervention: CalculatorEnum.Asthma,
            value: { ...form.values, isValid },
          })
        );
        dispatch(calculatorActions.setSummaryData());
      }
    });
  }, [form.values]);

  const onSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      calculatorActions.toggleInterventionsChecklistValue({
        key: InterventionsChecklistForm.Asthma,
        value: e.target.checked,
      })
    );
  };

  const onNote = (noteName: string, noteAction: 'add' | 'edit' | 'delete') => {
    setNoteName(noteName);
    setNoteAction(noteAction);
    setIsNoteDialogOpen(true);
  };

  const onDialogNoteClose = () => {
    setIsNoteDialogOpen(false);
  };

  return (
    <>
      <InterventionContainer
        disabled={!switchValue}
        onSubmit={form.handleSubmit}
        header={
          <InterventionHeader
            title="Asthma"
            checked={switchValue}
            onChange={onSwitchChange}
          />
        }
        body={
          <InterventionBody
            description={
              <>
                Some HS Specialists discuss asthma and wheezing with families.
                These conversations can help educate families about use of
                asthma-related medications, ensure they have their prescribed
                asthma-related medications available for use at home, and help
                avoid unnecessary ED visits as a result of an asthma or
                wheezing episode. The following questions ask about the actions
                taken by caregivers in these situations during the 12-month
                date range.
              </>
            }
            form={form}
            fields={fields}
            onNote={onNote}
          />
        }
        footer={
          <InterventionFooter
            hideLongTerm
            form={form}
            savingsText="Asthma"
            description="Estimated percent of Tier 2 and 3 HS Medicaid/CHIP-enrolled children who are able to avoid ED visits due to HS"
            onNote={onNote}
          />
        }
      />
      <InterventionFieldNoteDialog
        id={`${noteName}__${noteAction}-dialog`}
        name={noteName}
        value={form.values[noteName]}
        mode={noteAction}
        open={isNoteDialogOpen}
        onClose={onDialogNoteClose}
        form={form}
      />
    </>
  );
};

export default Asthma;
